import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Shield, Clock, Sparkles, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COPY = {
  trial:   { color: 'bg-blue-100 text-blue-800',       label: 'Free trial'   },
  active:  { color: 'bg-emerald-100 text-emerald-800', label: 'Active'       },
  expired: { color: 'bg-red-100 text-red-800',         label: 'Expired'      },
  unpaid:  { color: 'bg-amber-100 text-amber-800',     label: 'Trial ended'  },
  exempt:  { color: 'bg-slate-100 text-slate-800',     label: 'Admin'        },
};

// Loads the Razorpay Checkout.js script exactly once.
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingPlan, setPayingPlan] = useState(null);
  const [searchParams] = useSearchParams();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, h] = await Promise.all([
        axios.get(`${API}/billing/plans`),
        axios.get(`${API}/billing/status`, { withCredentials: true }),
        axios.get(`${API}/billing/history`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPlans(p.data.plans || []);
      setStatus(s.data);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load billing');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll after checkout closes — webhook is the source of truth so we wait
  // for the server to flip status="active".
  const pollForActivation = useCallback(async (planCode, attempts = 12) => {
    for (let i = 0; i < attempts; i++) {
      await new Promise(r => setTimeout(r, 2500));
      try {
        const { data } = await axios.get(`${API}/billing/status`, { withCredentials: true });
        setStatus(data);
        if (data.status === 'active' && data.current_plan === planCode) {
          toast.success('Payment confirmed — your subscription is now active!');
          const h = await axios.get(`${API}/billing/history`, { withCredentials: true }).catch(() => ({ data: [] }));
          setHistory(h.data || []);
          return true;
        }
      } catch (_) { /* retry */ }
    }
    toast.info('We’re still waiting on Razorpay’s confirmation. Refresh in a minute — no double charge, we activate only once the webhook verifies.');
    return false;
  }, []);

  const subscribe = async (plan) => {
    setPayingPlan(plan.code);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) { toast.error('Could not load Razorpay Checkout — check your connection.'); return; }

      const { data: order } = await axios.post(
        `${API}/billing/create-order`,
        { plan_code: plan.code },
        { withCredentials: true },
      );

      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: 'InsureGrowth',
        description: `${order.plan.label} subscription`,
        prefill: {
          name: order.customer.name || '',
          email: order.customer.email || '',
          contact: order.customer.contact || '',
        },
        theme: { color: '#0B7A3C' },
        // Server-side webhook is the source of truth — this handler just kicks
        // off polling so the UI reflects activation quickly.
        handler: () => {
          toast.success('Payment submitted — verifying with Razorpay…');
          pollForActivation(plan.code);
        },
        modal: {
          ondismiss: () => toast.info('Checkout closed. Your account remains in its current state — no charge unless payment completes.'),
        },
        notes: { plan_code: plan.code },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || 'Please try again'}`);
      });
      rzp.open();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 503) {
        toast.error(detail);  // "Razorpay is not configured yet…"
      } else {
        toast.error(detail || 'Could not start checkout');
      }
    } finally { setPayingPlan(null); }
  };

  // Deep-link support: `/billing?plan=quarterly` auto-scrolls the plan card into view.
  useEffect(() => {
    const target = searchParams.get('plan');
    if (target) {
      const el = document.querySelector(`[data-testid="plan-card-${target}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [searchParams, plans]);

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  const s = status || {};
  const statusMeta = STATUS_COPY[s.status] || STATUS_COPY.unpaid;
  const validUntil = s.subscription_end ? s.subscription_end.slice(0, 10) : (s.trial_ends_at ? s.trial_ends_at.slice(0, 10) : null);

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Billing &amp; Subscription</h1>
        <p className="text-slate-600 mt-1">Pick a plan that suits your book. Payments are processed securely by Razorpay.</p>
      </div>

      {/* Current status card */}
      <Card className="border-slate-200" data-testid="subscription-status-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle style={{ fontFamily: 'Manrope' }}><Shield className="inline h-5 w-5 mr-2 text-emerald-700" />Current status</CardTitle>
          <Badge className={statusMeta.color} data-testid="subscription-status-badge">{statusMeta.label}</Badge>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">State</p>
            <p className="text-lg font-semibold mt-1" data-testid="subscription-message">{s.message || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Current plan</p>
            <p className="text-lg font-semibold mt-1" data-testid="subscription-current-plan">
              {s.current_plan ? (plans.find(p => p.code === s.current_plan)?.label || s.current_plan) : 'None'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Valid until</p>
            <p className="text-lg font-semibold mt-1" data-testid="subscription-valid-until">{validUntil || '—'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map((plan) => {
          const isCurrent = s.current_plan === plan.code && s.status === 'active';
          const isBusy = payingPlan === plan.code;
          return (
            <Card
              key={plan.code}
              data-testid={`plan-card-${plan.code}`}
              className={`border-2 transition-all ${plan.code === 'yearly' ? 'border-emerald-600 shadow-md' : 'border-slate-200'}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Manrope' }}>{plan.label}</CardTitle>
                  {plan.code === 'yearly' && <Badge className="bg-emerald-600 text-white"><Sparkles className="h-3 w-3 mr-1" />Best value</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1">{plan.tagline}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    &#8377;{plan.price_inr.toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">One-time · {plan.duration_months}-month access</p>
                </div>
                <ul className="text-sm text-slate-700 space-y-2">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />Full CRM: leads, clients, policies, tasks</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />AI briefing, cross-sell &amp; win-back engine</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />Personal agent landing page &amp; lead forms</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />Commission tracking &amp; PDF quotations</li>
                </ul>
                <Button
                  onClick={() => subscribe(plan)}
                  disabled={isBusy || isCurrent}
                  data-testid={`subscribe-button-${plan.code}`}
                  className={`w-full ${plan.code === 'yearly' ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-blue-800 hover:bg-blue-900'} text-white`}
                >
                  {isCurrent ? 'Current plan' : isBusy ? 'Opening checkout…' : `Subscribe · ₹${plan.price_inr.toLocaleString('en-IN')}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Payment history */}
      <Card className="border-slate-200" data-testid="payment-history-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}><Clock className="inline h-5 w-5 mr-2 text-slate-500" />Payment history</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No payments yet. Your invoices will appear here once you subscribe.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 uppercase tracking-wider border-b">
                  <tr>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Payment ID</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.order_id} className="border-b last:border-0" data-testid={`payment-row-${row.order_id}`}>
                      <td className="py-2 pr-4">{(row.created_at || '').slice(0, 10)}</td>
                      <td className="py-2 pr-4">{row.plan_label}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(row.amount_inr || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2 pr-4">
                        <Badge className={
                          row.status === 'captured' ? 'bg-emerald-100 text-emerald-800' :
                          row.status === 'failed'   ? 'bg-red-100 text-red-800' :
                                                      'bg-slate-100 text-slate-700'
                        }>{row.status}</Badge>
                      </td>
                      <td className="py-2 font-mono text-xs text-slate-500">{row.payment_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer note */}
      <div className="text-xs text-slate-500 flex items-center gap-2">
        <ExternalLink className="h-3 w-3" />
        Payments are processed by Razorpay. Your card details never touch our servers.
      </div>
    </div>
  );
}
